import React, { useEffect, useState } from 'react';
import { Modal, Button, Form } from 'react-bootstrap';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import Select from 'react-select';
import { FaEdit, FaTrash, FaPlus } from 'react-icons/fa';
import 'bootstrap/dist/css/bootstrap.min.css';
import { useDispatch, useSelector } from 'react-redux';
import {
    fetchAllUserRoles,
    fetchAllPrivileges,
    deleteUserByUserId,
    updateUserRoleById,
    // <-- import for role creation
} from '../redux/actions/userAction';

import {
    createNewRole
} from '../redux/actions/UserRoleAction';

function UserRoleManagement() {
    const dispatch = useDispatch();

    const rawUserRoles = useSelector((state) => state.userRoles) || {};
    const userRoles = Array.isArray(rawUserRoles.userRoles)
        ? rawUserRoles.userRoles
        : Array.isArray(rawUserRoles)
            ? rawUserRoles
            : [];

    const privileges = useSelector((state) =>
        Array.isArray(state.privileges?.privileges)
            ? state.privileges.privileges
            : Array.isArray(state.privileges?.results)
                ? state.privileges.results
                : []
    );

    const [roles, setRoles] = useState([]);
    const [showModal, setShowModal] = useState(false);
    const [currentRole, setCurrentRole] = useState({ id: null, name: '', privilege_actions_ids: [] });
    const [saveLoading, setSaveLoading] = useState(false);
    const [isCreating, setIsCreating] = useState(false);

    useEffect(() => {
        dispatch(fetchAllUserRoles());
        dispatch(fetchAllPrivileges());
    }, [dispatch]);

    useEffect(() => {
        setRoles(userRoles);
    }, [userRoles]);

    const privilegeOptions = Array.isArray(privileges)
        ? privileges.map(priv => ({
            value: priv.id,
            label: priv.name
        }))
        : [];

    // For creating a new role: reset modal values and flag as create mode
    const openAddModal = () => {
        setCurrentRole({ id: null, name: '', privilege_actions_ids: [] });
        setIsCreating(true);
        setShowModal(true);
    };

    // For editing: fill modal with data and flag as edit
    const openModal = (role = { id: null, name: '', privilege_actions: [], privilege_actions_ids: [] }) => {
        setCurrentRole({
            ...role,
            privilege_actions_ids: Array.isArray(role.privilege_actions)
                ? role.privilege_actions.map(p => typeof p === 'object' ? p.id : p)
                : [],
        });
        setIsCreating(false);
        setShowModal(true);
    };

    const closeModal = () => setShowModal(false);

    const handleInputChange = e => {
        const { name, value } = e.target;
        setCurrentRole(prev => ({ ...prev, [name]: value }));
    };

    const handlePrivilegesChange = selectedOptions => {
        setCurrentRole(prev => ({
            ...prev,
            privilege_actions_ids: selectedOptions.map(opt => opt.value),
        }));
    };

    const handleSave = async () => {
        setSaveLoading(true);
        try {
            if (isCreating) {
                if (currentRole.name) {
                    await dispatch(createNewRole({
                        name: currentRole.name,
                        privilege_actions_ids: currentRole.privilege_actions_ids,
                    }));
                    dispatch(fetchAllUserRoles());
                }
            } else {
                if (currentRole.id && currentRole.name) {
                    await dispatch(updateUserRoleById(currentRole.id, {
                        name: currentRole.name,
                        privilege_actions_ids: currentRole.privilege_actions_ids,
                    }));
                    dispatch(fetchAllUserRoles());
                }
            }
            setShowModal(false);
        } catch (error) {
            alert("Failed to save changes");
        }
        setSaveLoading(false);
    };

    const handleDelete = (roleId) => {
        if (window.confirm("Are you sure you want to delete this role?")) {
            dispatch(deleteUserByUserId(roleId));
            dispatch(fetchAllUserRoles());
        }
    };

    const privilegeNameBody = rowData => {
        if (Array.isArray(rowData.privilege_actions) && rowData.privilege_actions.length > 0) {
            const flatNames = [];
            rowData.privilege_actions.forEach(priv => {
                if (typeof priv === "object" && priv !== null) {
                    flatNames.push(priv.name);
                    if (Array.isArray(priv.subprivilages) && priv.subprivilages.length > 0) {
                        flatNames.push(...priv.subprivilages.map(sub => sub.name));
                    }
                } else {
                    const match = privileges.find(p => p.id === priv);
                    flatNames.push(match ? match.name : priv);
                }
            });
            return flatNames.join(", ");
        }
        return <span className="text-muted">No privileges</span>;
    };

    const actionBodyTemplate = rowData => (
        <div className="d-flex gap-2 align-items-center">
            <Button variant="warning" size="sm" className="p-1" onClick={() => openModal(rowData)}>
                <FaEdit className="me-1" /> Edit
            </Button>
            <Button variant="danger" size="sm" className="p-1" onClick={() => handleDelete(rowData.id)}>
                <FaTrash className="me-1" /> Delete
            </Button>
        </div>
    );

    return (
        <div className="container mt-4">
            <div className="d-flex justify-content-between align-items-center mb-3">
                <h2 className="mb-0">User Role Management</h2>
                <Button
                    variant="success"
                    size="md"
                    style={{
                        display: "flex",
                        alignItems: "center",
                        borderRadius: "16px",
                        padding: "0.5rem 1.3rem",
                        fontWeight: 300,
                        fontSize: "16px",
                        boxShadow: "0 2px 6px rgba(40,167,69,0.10)",
                        transition: "transform 0.15s, box-shadow 0.15s",
                        backgroundColor: "#28a745",
                    }}
                    onMouseOver={e => {
                        e.currentTarget.style.backgroundColor = "#219150";
                        e.currentTarget.style.transform = "scale(1.03)";
                        e.currentTarget.style.boxShadow = "0 4px 12px rgba(40,167,69,0.18)";
                    }}
                    onMouseOut={e => {
                        e.currentTarget.style.backgroundColor = "";
                        e.currentTarget.style.transform = "scale(1)";
                        e.currentTarget.style.boxShadow = "0 2px 6px rgba(40,167,69,0.10)";
                    }}
                    onClick={openAddModal}
                >
                    <FaPlus className="me-2" style={{ fontSize: "1.0em" }} />
                    Add Role
                </Button>

            </div>
            <DataTable value={roles} paginator rows={5} responsiveLayout="scroll" rowHover>
                <Column field="id" header="ID" />
                <Column field="name" header="Role Name" />
                <Column header="Privileges" body={privilegeNameBody} />
                <Column header="Actions" body={actionBodyTemplate} />
            </DataTable>
            <Modal show={showModal} onHide={closeModal}>
                <Modal.Header closeButton>
                    <Modal.Title>{isCreating ? "Add Role" : (currentRole.id ? 'Edit Role' : 'Add Role')}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Form>
                        <Form.Group className="mb-3">
                            <Form.Label>Name</Form.Label>
                            <Form.Control
                                type="text"
                                name="name"
                                value={currentRole.name}
                                onChange={handleInputChange}
                                placeholder="Enter role name"
                            />
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Label>Privileges</Form.Label>
                            <Select
                                isMulti
                                options={privilegeOptions}
                                value={
                                    privilegeOptions.filter(opt =>
                                        Array.isArray(currentRole.privilege_actions_ids) &&
                                        currentRole.privilege_actions_ids.includes(opt.value)
                                    )
                                }
                                onChange={handlePrivilegesChange}
                                placeholder="Select privileges for this role"
                            />
                            <div className="mt-2">
                                {privilegeOptions
                                    .filter(opt =>
                                        Array.isArray(currentRole.privilege_actions_ids) &&
                                        currentRole.privilege_actions_ids.includes(opt.value)
                                    )
                                    .map(opt => (
                                        <div key={opt.value}>{opt.label}</div>
                                    ))}
                            </div>
                        </Form.Group>
                    </Form>
                </Modal.Body>
                <Modal.Footer className="d-flex justify-content-end gap-2">
                    <Button variant="secondary" onClick={closeModal}>
                        Cancel
                    </Button>
                    <Button variant="primary" onClick={handleSave} disabled={saveLoading}>
                        {saveLoading ? "Saving..." : "Save"}
                    </Button>
                </Modal.Footer>
            </Modal>
        </div>
    );
}

export default UserRoleManagement;
